import { useState } from 'react';

import { Input } from '@/components/ui/input';
import type { ClinicalSubject } from './types';
import { Button } from '@/components/ui/button';
import { OfflinePreparation } from './OfflinePreparationPanel';
import { Field, FieldLabel } from '@/components/ui/field';
import { OfflineQueueReview } from './OfflineQueueReview';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { OfflineFlowsheetController } from './useOfflineFlowsheet';
export function OfflineControls({
  controller,
  subjects = [],
}: {
  controller: OfflineFlowsheetController;
  subjects?: ClinicalSubject[];
}) {
  const [passphrase, setPassphrase] = useState('');
  const { busy, status, notice, pending, unlocked } = controller;
  return (
    <section aria-label="Offline storage" className="flex flex-col gap-3 rounded-lg border p-4">
      <h3 className="font-semibold">Offline storage for this open episode</h3>
      <p className="text-sm text-muted-foreground">
        Prepare while connected, then bookmark the offline records page. Use a separate offline passphrase; forgotten
        passphrases cannot currently be recovered.
      </p>
      {!unlocked ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void controller
              .unlock(passphrase)
              .then(() => setPassphrase(''))
              .catch(() => undefined);
          }}>
          <Field className="max-w-md">
            <FieldLabel htmlFor="offline-passphrase">Offline passphrase (at least 12 characters)</FieldLabel>
            <Input
              id="offline-passphrase"
              required
              minLength={12}
              disabled={busy}
              type="password"
              autoComplete="off"
              value={passphrase}
              className="min-h-11"
              onChange={(event) => setPassphrase(event.target.value)}
            />
          </Field>
          <Button type="submit" disabled={busy} className="min-h-11">
            {busy ? 'Preparing…' : 'Unlock / prepare offline storage'}
          </Button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <p role="status">
            {status.pending
              ? `${status.pending} saved batch${status.pending === 1 ? '' : 'es'} awaiting sync`
              : 'All locally saved entries synced'}
          </p>
          <Button disabled={busy} className="min-h-11" onClick={() => void controller.sync()}>
            {busy ? 'Synchronizing…' : 'Sync now'}
          </Button>
          <Button variant="outline" className="min-h-11" onClick={() => void controller.exportRecovery()}>
            Download encrypted recovery file
          </Button>
          <Button variant="outline" className="min-h-11" onClick={controller.lock}>
            Lock offline records
          </Button>
        </div>
      )}
      {unlocked && !controller.cacheReady && (
        <p role="alert">
          New local entries require current offline authorization. Connect and sync to renew; cached records and pending
          entries remain retained.
        </p>
      )}
      {!!status.signInRequired && (
        <p role="alert">Sign in again to synchronize. Your encrypted pending entries are retained.</p>
      )}
      {!!(status.retryableErrors || status.receiptMismatches) && (
        <p role="alert">Some entries could not be acknowledged. Retry synchronization; do not enter them again.</p>
      )}
      {unlocked && <OfflinePreparation controller={controller} />}
      {unlocked && (
        <a target="_blank" className="underline" rel="noopener noreferrer" href="/offline-flowsheets.html">
          Open prepared offline records
        </a>
      )}
      {notice && (
        <Alert>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}
      {unlocked && (
        <p>
          {controller.resourcesReady
            ? 'Episode, event definitions and practice configuration prepared.'
            : 'Practice configuration is not prepared yet; reconnect to finish preparation.'}
        </p>
      )}
      {controller.offlineSince && (
        <p role="status">
          Offline since {new Date(controller.offlineSince).toLocaleTimeString()} · entries stay on this device until
          acknowledged.
        </p>
      )}
      {controller.clock?.warning && (
        <Alert>
          <AlertDescription>
            Device clock differs from the server by approximately{' '}
            {Math.round(Math.abs(controller.clock.offsetMs) / 60000)} minutes. Check the device clock. Recorded clinical
            times have not been changed.
          </AlertDescription>
        </Alert>
      )}
      {unlocked && pending.length > 0 && (
        <OfflineQueueReview disabled={busy} pending={pending} subjects={subjects} onResolve={controller.resolve} />
      )}
    </section>
  );
}
